import type { Exercise, MovementPattern } from '../data/exercise-types';

/**
 * Warm-up ramps.
 *
 * The plan only ever prescribed working sets, which quietly assumed you'd
 * improvise the ramp yourself. You shouldn't have to: walking up to a heavy
 * squat cold is how people get hurt, and too many warm-up sets eats the energy
 * you needed for the work.
 *
 * The ramp scales with how heavy the top set is. A 60 kg bench gets two steps;
 * a 140 kg squat gets four. Warm-ups are never counted as working sets, so they
 * don't touch your weekly volume or your progression history.
 */

export interface WarmupSet {
  weightKg: number;
  reps: number;
  /** "Empty bar", "50%" — shown next to the row. */
  label: string;
}

export const DEFAULT_BAR_KG = 20;

/** Barbells jump in 2.5 kg; machines and cables are coarser and vary. */
function roundFor(exercise: Exercise, weight: number): number {
  const step = exercise.loadType === 'barbell' ? 2.5 : Math.max(2.5, exercise.incrementKg);
  return Math.round(weight / step) * step;
}

/**
 * Ramp shape by how far the working weight is above the empty bar. Lighter
 * lifts need fewer steps — three warm-up sets to reach 45 kg is just fatigue.
 */
function rampFor(ratio: number): { pct: number; reps: number }[] {
  if (ratio >= 5) {
    return [
      { pct: 0.4, reps: 5 },
      { pct: 0.6, reps: 3 },
      { pct: 0.75, reps: 2 },
      { pct: 0.9, reps: 1 },
    ];
  }
  if (ratio >= 3) {
    return [
      { pct: 0.5, reps: 5 },
      { pct: 0.7, reps: 3 },
      { pct: 0.85, reps: 2 },
    ];
  }
  return [
    { pct: 0.5, reps: 5 },
    { pct: 0.75, reps: 3 },
  ];
}

/**
 * Builds the ramp for one exercise at a given working weight.
 *
 * Returns an empty list when a warm-up would be pointless — bodyweight and
 * timed work, unknown loads, or anything already close to an empty bar.
 */
export function warmupPlan(
  exercise: Exercise,
  workingWeightKg: number | undefined,
  barKg: number = DEFAULT_BAR_KG,
): WarmupSet[] {
  if (!workingWeightKg || workingWeightKg <= 0) return [];
  if (exercise.loadType === 'bodyweight' || exercise.loadType === 'timed') return [];
  if (exercise.loadType === 'assisted') return [];

  const isBarbell = exercise.loadType === 'barbell';
  // Machines and dumbbells have no bar to start from, so ramp from zero.
  const base = isBarbell ? barKg : 0;

  if (workingWeightKg <= base * 1.35) return [];

  const sets: WarmupSet[] = [];

  // Barbell work starts with the empty bar to groove the pattern.
  if (isBarbell && workingWeightKg >= barKg * 2) {
    sets.push({ weightKg: barKg, reps: 8, label: 'Empty bar' });
  }

  const ratio = isBarbell ? workingWeightKg / barKg : 4;

  for (const step of rampFor(ratio)) {
    const weight = roundFor(exercise, workingWeightKg * step.pct);
    // Skip anything that isn't a real step up from what's already loaded.
    const previous = sets[sets.length - 1]?.weightKg ?? base;
    if (weight <= previous) continue;
    if (weight >= workingWeightKg) continue;
    sets.push({
      weightKg: weight,
      reps: step.reps,
      label: `${Math.round(step.pct * 100)}%`,
    });
  }

  return sets;
}

/**
 * Which exercises in a session are worth warming up.
 *
 * Only compound lifts, and only the first time you hit each movement pattern —
 * once you've pressed, you're warm for pressing. Isolation work at the end of a
 * session needs nothing.
 */
export function warmupTargets(
  exercises: { exerciseId: string; exercise: Exercise | undefined }[],
): Set<string> {
  const seen = new Set<MovementPattern>();
  const targets = new Set<string>();

  for (const { exerciseId, exercise } of exercises) {
    if (!exercise || exercise.kind !== 'compound') continue;
    if (exercise.loadType === 'bodyweight' || exercise.loadType === 'timed') continue;
    if (seen.has(exercise.pattern)) continue;
    seen.add(exercise.pattern);
    targets.add(exerciseId);
  }

  return targets;
}
